import { t } from 'i18next'

import Panel from '~/components/Panel'

import {
  AudienceGender,
  AudienceRegion,
  AudienceSocialClass
} from '../../constants/audienceTargeting'
import {
  asTranslate,
  buildAudienceTargetingLabels,
  buildInterestLabels
} from '../../helpers/audienceTargetingSummary'
import { BriefingRows, BriefingRow } from '../BriefingRows'

/** O `t` adaptado uma vez, no módulo: as chaves aqui são montadas em runtime. */
const translate = asTranslate(t)

type Props = {
  targeting?: CampaignSettings['audience_targeting']
  channel?: CampaignChannelValue | null
  /** Presença de `audience_id` na campanha, não o conteúdo da audiência. */
  hasImportedAudience?: boolean
}

/**
 * Cartão "Audiência e segmentação" da revisão offsite.
 *
 * Traduz a forma snake_case de `settings.audience_targeting` para o resumo que
 * `ReviewStep` já usava na criação, então as duas telas concordam sobre quando
 * uma seleção vira "Todas" e sobre como cada valor é escrito.
 *
 * Diferença deliberada em relação à criação: ali a linha de segmentação mostra
 * só quais travas estão ativas, porque o formulário não tem os valores
 * consolidados. Aqui a resposta traz os interesses, e quem aprova precisa ver
 * o que foi segmentado, não apenas que houve segmentação — então os dois
 * aparecem, a composição das travas e a lista.
 */
export const AudienceSegmentation = ({
  targeting,
  channel,
  hasImportedAudience
}: Props) => {
  const labels = buildAudienceTargetingLabels(
    {
      region: targeting?.region as AudienceRegion[],
      ageRange: targeting?.age_range,
      socialClass: targeting?.social_class as AudienceSocialClass[],
      gender: targeting?.gender as AudienceGender[]
    },
    translate
  )

  const interests = buildInterestLabels(targeting?.interests, translate)

  /**
   * Trava de audiência importada nomeada pelo canal, como o formulário faz:
   * "Conversão VTEX" no Meta, "Audiência VTEX" no DV360.
   */
  const importTitleKey =
    channel === 'meta'
      ? 'rm:campaign.create.AudienceTargeting.audienceImport.meta.title'
      : 'rm:campaign.create.AudienceTargeting.audienceImport.dv360.title'

  const activeGates = [
    interests.length
      ? t('rm:campaign.create.AudienceTargeting.interests.title')
      : null,
    hasImportedAudience ? t(importTitleKey) : null
  ].filter(Boolean)

  const rows: BriefingRow[] = [
    { label: t('rm:campaign.create.ReviewStep.region'), value: labels.region },
    {
      label: t('rm:campaign.create.ReviewStep.ageRange'),
      value: labels.ageRange
    },
    { label: t('rm:campaign.create.ReviewStep.gender'), value: labels.gender },
    {
      label: t('rm:campaign.create.ReviewStep.socialClass'),
      value: labels.socialClass
    },
    {
      label: t('rm:campaign.create.ReviewStep.segmentation'),
      // Sem trava alguma a campanha roda em alcance amplo, que é informação, não
      // ausência de dado — daí a frase em vez do travessão.
      value: activeGates.length
        ? activeGates.join(' · ')
        : t('rm:offsiteAdvertiserReview.detail.broadAudience')
    }
  ]

  if (interests.length) {
    rows.push({
      label: t('rm:campaign.create.AudienceTargeting.interests.title'),
      value: interests.join(', ')
    })
  }

  return (
    <Panel
      template="shoreline"
      title={t('rm:campaign.create.AudienceTargeting.title')}
      isCollapsible
    >
      <BriefingRows rows={rows} layout="grid" />
    </Panel>
  )
}
